import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { hypecastHttpContext } from "@app/core/http/http-context";

import { Podcast, PodcastCreateDto, PodcastUpdateDto } from "../models/classes/Podcast";
import { EntityService } from "./entity.service";
import { Observable } from "rxjs";
import { Response } from "@app/shared/services/local/Response";
import { map } from "rxjs/operators";
import { Episode } from "@app/shared/models/classes/Episode";
import { CacheSettings } from "@app/core/http/cache.interceptor";

@Injectable()
export class PodcastsService extends EntityService<Podcast, PodcastCreateDto, PodcastUpdateDto> {
  constructor(protected httpClient: HttpClient) {
    super("podcasts", "Podcast", httpClient);
  }

  getAllCoyo(
    cacheOptions: CacheSettings | null = null,
    token: string | null = null,
    sorting: string = "-modified"
  ): Observable<Podcast[]> {
    const headers = { Authorization: "Bearer " + token };
    return this.httpClient.get<Response<Podcast[]>>(
      `podcasts/coyo?orderby=${sorting}`,
      {headers, context: hypecastHttpContext({cache: cacheOptions, disableAccessToken: true})},
    ).pipe(map((response: Response<Podcast[]>) => response.data));
  }

  getCoyoPodcastById(
    cacheOptions: CacheSettings | null = null,
    podcastId: string | null = null,
    token: string | null = null
  ): Observable<Podcast> {
    const headers = { Authorization: "Bearer " + token };
    return this.httpClient.get<Response<Podcast>>(
      `podcasts/coyo/${podcastId}`,
      {headers, context: hypecastHttpContext({cache: cacheOptions, disableAccessToken: true})},
    ).pipe(map((response: Response<Podcast>) => response.data));
  }

  getAllEpisodesForCoyoPodcast(
    cacheOptions: CacheSettings | null = null,
    podcastId: string | null = null,
    token: string | null = null
  ): Observable<Episode[]> {
    const headers = { Authorization: "Bearer " + token };
    return this.httpClient.get<Response<Episode[]>>(
      `podcasts/coyo/${podcastId}/episodes`,
      {headers, context: hypecastHttpContext({cache: cacheOptions, disableAccessToken: true})},
    ).pipe(
      map((response: Response<Episode[]>) => response.data.sort((a: Episode, b: Episode) =>
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()))
    );
  }

  getCoyoPodcastEpisodesPage(
    podcastId: string,
    token: string,
    skip: number,
    take: number
  ): Observable<Episode[]> {
    const headers = { Authorization: "Bearer " + token };
    const params = new HttpParams()
      .set("skip", skip.toString())
      .set("take", take.toString())
      // Sort contract for episodic content: release date, not the import-clustered publish date.
      // Order breaks same-minute ties; without it skip/take paging can show an episode twice or never.
      .set("orderby", "-ValidFrom,-Order");
    return this.httpClient.get<Response<Episode[]>>(
      `podcasts/coyo/${podcastId}/episodes`,
      {headers, params, context: hypecastHttpContext({cache: null, disableAccessToken: true})},
    ).pipe(map((response: Response<Episode[]>) => response.data));
  }

  getIframePodcastById(cacheOptions: CacheSettings | null = null, podcastId: string | null = null): Observable<Podcast> {
    return this.httpClient.get<Response<Podcast>>(
      `podcasts/iframe/${podcastId}`,
      {context: hypecastHttpContext({cache: cacheOptions, disableAccessToken: true})},
    ).pipe(map((response: Response<Podcast>) => response.data));
  }

  getAllEpisodesForIframePodcast(cacheOptions: CacheSettings | null = null, podcastId: string | null = null): Observable<Episode[]> {
    return this.httpClient.get<Response<Episode[]>>(
      `podcasts/iframe/${podcastId}/episodes`,
      {context: hypecastHttpContext({cache: cacheOptions, disableAccessToken: true})},
    ).pipe(
      map((response: Response<Episode[]>) => response.data.sort((a: Episode, b: Episode) =>
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()))
    );
  }
}
